/**
 * Formatted Message Component
 * Renders Markdown content from chatbot responses
 * Handles <answer> XML wrapper and converts Markdown to formatted HTML
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface FormattedMessageProps {
  content: string;
  imageAltFallback?: string;
}

const ImageRenderer = ({ src, alt, imageAltFallback = 'Image' }: { src?: string; alt?: string; imageAltFallback?: string }) => {
  const [imageError, setImageError] = React.useState(false);
  const safeSrc = src ? src.trim() : null;

  if (!safeSrc || imageError) {
    return null;
  }

  return (
    <div className="chatbot-image-container">
      <a href={safeSrc} target="_blank" rel="noopener noreferrer" className="chatbot-image-link">
        <img
          src={safeSrc}
          alt={alt || imageAltFallback}
          loading="lazy"
          onError={() => setImageError(true)}
          className="chatbot-image"
        />
      </a>
    </div>
  );
};

 const LinkRenderer = ({ href, children }: { href?: string; children?: React.ReactNode }) => {
  const safeHref = href ? href.trim() : null;
  if (!safeHref) return <span>{children}</span>;

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className="chatbot-link"
    >
      {children}
    </a>
  );
};

 const CodeRenderer = ({ inline, children }: { inline?: boolean; children?: React.ReactNode }) => {
  if (inline) {
    return <code className="chatbot-code-inline">{children}</code>;
  }
  return (
    <pre className="chatbot-code-block">
      <code>{children}</code>
    </pre>
  );
};

 const HeadingRenderer = ({ level, children }: { level: number; children?: React.ReactNode }) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  return <Tag className={`chatbot-heading chatbot-heading-${level}`}>{children}</Tag>;
};

 const ParagraphRenderer = ({ children }: { children?: React.ReactNode }) => {
  return <p className="chatbot-paragraph">{children}</p>;
};

 const ListRenderer = ({ ordered, children }: { ordered?: boolean; children?: React.ReactNode }) => {
  if (ordered) {
    return <ol className="chatbot-list chatbot-list-ordered">{children}</ol>;
  }
  return <ul className="chatbot-list chatbot-list-unordered">{children}</ul>;
};

 const ListItemRenderer = ({ children }: { children?: React.ReactNode }) => {
  return <li className="chatbot-list-item">{children}</li>;
};

 const BlockquoteRenderer = ({ children }: { children?: React.ReactNode }) => {
  return <blockquote className="chatbot-blockquote">{children}</blockquote>;
};

 const StrongRenderer = ({ children }: { children?: React.ReactNode }) => {
  return <strong className="chatbot-strong">{children}</strong>;
};

 const EmRenderer = ({ children }: { children?: React.ReactNode }) => {
  return <em className="chatbot-em">{children}</em>;
};

const extractAnswerContent = (text = ""): string => {
  const answerMatch = text.match(/<answer>([\s\S]*?)<\/answer>/);
  return answerMatch ? answerMatch[1].trim() : text;
};

const normalizeCitations = (text = ""): string => {
  return text
    .replace(/([^\n])\n\*\*Copilot knowledge base citations?:?\*\*/gi, '$1\n\n**Copilot knowledge base citations**')
    .replace(/\*\*Copilot knowledge base citations?:?\*\*/gi, '**Copilot knowledge base citations**')
    .replace(/\n(-\s*\[\d+\]\s+)/g, '\n$1');
};

  const normalizeMarkdown = (text = ""): string => {
    return text
      // Fix headings missing space after #: ###Title → ### Title
      .replace(/^(#{1,6})([^\s#])/gm, '$1 $2')
      // Fix list items missing space after dash: -**text** → - **text**
      .replace(/^(\s*)-(\S)/gm, '$1- $2')
      // Fix list items missing space after asterisk: *text → * text
      .replace(/^(\s*)\*([^\s*])/gm, '$1* $2');
  };

export const FormattedMessage: React.FC<FormattedMessageProps> = ({
  content,
  imageAltFallback = 'Image',
}) => {
    const markdownContent = normalizeMarkdown(normalizeCitations(extractAnswerContent(content)));

  return (
    <div className="chatbot-formatted-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          img: (props) => <ImageRenderer {...props} imageAltFallback={imageAltFallback} />,
          a: LinkRenderer as any,
          code: CodeRenderer as any,
          h1: (props) => <HeadingRenderer level={1} {...props} />,
          h2: (props) => <HeadingRenderer level={2} {...props} />,
          h3: (props) => <HeadingRenderer level={3} {...props} />,
          h4: (props) => <HeadingRenderer level={4} {...props} />,
          h5: (props) => <HeadingRenderer level={5} {...props} />,
          h6: (props) => <HeadingRenderer level={6} {...props} />,
          p: ParagraphRenderer as any,
          ul: ListRenderer as any,
          ol: ListRenderer as any,
          li: ListItemRenderer as any,
          blockquote: BlockquoteRenderer as any,
          strong: StrongRenderer as any,
          em: EmRenderer as any,
        }}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
};
